import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { AdminNav } from "@/components/AdminNav";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";

const NZ_TZ = "Pacific/Auckland";
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function todayNZ() {
  const s = new Date().toLocaleDateString("en-CA", { timeZone: NZ_TZ }); // YYYY-MM-DD
  return s;
}

export default function AdminCalendar() {
  const nav = useNavigate();
  const [entries, setEntries] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => { const [y, m] = todayNZ().split("-"); return { y: +y, m: +m }; });

  const load = useCallback(async () => {
    if (!localStorage.getItem("mrshu_admin_token")) { nav("/admin/login"); return; }
    try { const { data } = await adminApi.get("/admin/calendar"); setEntries(data); }
    catch (e) { if ([401, 403].includes(e?.response?.status)) nav("/admin/login"); }
  }, [nav]);
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);

  const byDate = {};
  entries.forEach((e) => { if (e.date) { (byDate[e.date] = byDate[e.date] || []).push(e); } });

  const monthKey = `${cursor.y}-${String(cursor.m).padStart(2, "0")}`;
  const firstDay = new Date(cursor.y, cursor.m - 1, 1);
  const startWeekday = firstDay.getDay();
  const daysInMonth = new Date(cursor.y, cursor.m, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const shift = (delta) => setCursor((c) => {
    let m = c.m + delta, y = c.y;
    if (m < 1) { m = 12; y--; } if (m > 12) { m = 1; y++; }
    return { y, m };
  });

  const EntryChip = ({ e }) => {
    const inspection = e.type === "On-site Inspection";
    const confirmed = e.state === "Confirmed";
    return (
      <button data-testid={`cal-entry-${e.ref_number}`} onClick={() => nav(`/admin/jobs/${e.job_id}`)}
        className={`block w-full truncate rounded-md px-1.5 py-0.5 text-left text-[11px] font-medium border-l-4 ${confirmed ? "bg-primary/10 border-primary text-primary" : "bg-accent/60 border-amber-500 text-amber-800"} ${inspection ? "" : "italic"}`}>
        {e.urgency === "Urgent" && <AlertTriangle className="inline h-3 w-3 text-destructive mr-0.5" />}
        {e.ref_number} · {inspection ? "Insp" : "Work"} · {e.slot || ""} {confirmed ? "✓" : "?"}
      </button>
    );
  };

  const upcoming = [...entries].filter((e) => e.date >= todayNZ()).sort((a, b) => (a.date + (a.slot || "")).localeCompare(b.date + (b.slot || "")));

  return (
    <div className="min-h-screen bg-background">
      <AdminNav active="calendar" />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display text-2xl font-bold text-primary">Calendar <span className="text-sm font-normal text-muted-foreground">({NZ_TZ})</span></h2>
          <div className="flex items-center gap-2">
            {["month", "week", "day"].map((v) => (
              <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)} className={`rounded-full px-3 py-1.5 text-sm font-medium ${view === v ? "bg-primary text-primary-foreground" : "bg-white border border-border"}`}>{v[0].toUpperCase() + v.slice(1)}</button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-primary/10 border-l-4 border-primary" /> Confirmed</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-accent/60 border-l-4 border-amber-500" /> Proposed</span>
          <span className="italic">italic = Work Visit</span>
        </div>

        {view === "month" ? (
          <>
            <div className="mt-4 flex items-center justify-between">
              <button data-testid="cal-prev" onClick={() => shift(-1)} className="rounded-full border border-border p-2"><ChevronLeft className="h-4 w-4" /></button>
              <p className="font-display font-bold text-primary" data-testid="cal-month-label">{firstDay.toLocaleDateString("en-NZ", { month: "long", year: "numeric" })}</p>
              <button data-testid="cal-next" onClick={() => shift(1)} className="rounded-full border border-border p-2"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 grid grid-cols-7 gap-1 text-center text-xs font-semibold text-muted-foreground">{DOW.map((d) => <div key={d} className="py-1">{d}</div>)}</div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                const dateStr = d ? `${monthKey}-${String(d).padStart(2, "0")}` : null;
                const dayEntries = dateStr ? (byDate[dateStr] || []) : [];
                return (
                  <div key={i} className={`min-h-[84px] rounded-lg border p-1 ${dateStr === todayNZ() ? "border-primary bg-secondary/40" : "border-border bg-white"} ${!d ? "opacity-0" : ""}`}>
                    {d && <p className="text-[11px] text-muted-foreground mb-0.5">{d}</p>}
                    <div className="space-y-0.5">{dayEntries.map((e, j) => <EntryChip key={j} e={e} />)}</div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="mt-6 space-y-3" data-testid="cal-list">
            {upcoming.length === 0 ? <p className="text-muted-foreground">No upcoming appointments.</p> : upcoming.map((e, i) => (
              <button key={i} data-testid={`cal-list-${e.ref_number}`} onClick={() => nav(`/admin/jobs/${e.job_id}`)} className="flex w-full items-center justify-between rounded-2xl border border-border bg-white p-4 text-left shadow-sm hover:border-primary/40">
                <div>
                  <p className="font-semibold text-primary">{e.ref_number} · {e.customer}</p>
                  <p className="text-sm text-muted-foreground">{e.type} · {e.date} · {e.slot || ""}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${e.state === "Confirmed" ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"}`}>{e.state}</span>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
